import { TokenResponse, useGoogleLogin } from '@react-oauth/google';
import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { trackSignUpCompleted } from '../../lib/analytics';
import Button from '../Button';
import { completeLogin } from '../Login/completeLogin';
import { TWO_FACTOR_LOGIN_PATH, TWO_FACTOR_REQUIRED } from '../Login/twoFactorMutations';
import { loginOrSignUpWithGoogle } from './mutations';

import iconSvg from './icon.svg';
import { showSignupRedirectModal } from '../Signup/SignupRedirectModal';
import { getApolloErrorMessageCodes } from '../../lib/errors';

interface Props {
  showError: (error: string | null) => void;
  type: string;
}

type GoogleResponse = Omit<TokenResponse, 'error' | 'error_description' | 'error_uri'>;

export default function GoogleSignIn({ type, showError }: Props) {
  const history = useHistory();
  const { search } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const isSignupPage = type === 'signup';

  const handleResponse = useCallback(
    async (googleResponse?: GoogleResponse) => {
      showError(null);
      setIsLoading(true);

      try {
        if (!googleResponse) {
          return showError('Unable to verify with google.');
        }

        const { user, isSignup, userHash } = await loginOrSignUpWithGoogle({
          token: googleResponse.access_token,
        });
        const { email, id } = user;

        completeLogin({ id, email, userHash });

        if (isSignup) {
          trackSignUpCompleted(
            {
              location: 'sign_up',
              type: 'google_verification',
              onboarding_bucket: 'default',
              checkout_source: 'default',
            },
            email,
          );

          history.push('/purchase');
        }
      } catch (e) {
        const errorCodes = getApolloErrorMessageCodes(e);
        if (errorCodes?.includes('USER_NOT_APPROVED')) {
          showSignupRedirectModal();
          return;
        }
        if (errorCodes?.includes(TWO_FACTOR_REQUIRED)) {
          // keeps the search, so that ?redirect= still applies after the second step
          history.push({ pathname: TWO_FACTOR_LOGIN_PATH, search });
          return;
        }
        showError('Unable to verify with google.');
      } finally {
        setIsLoading(false);
      }
    },
    [history, search, showError],
  );

  const login = useGoogleLogin({
    onSuccess: (response) => handleResponse(response),
    onError: () => handleResponse(),
    onNonOAuthError: () => handleResponse(),
  });

  return (
    <div className="max-w-lg mx-auto mt-4">
      <Button
        size="lg"
        fullWidth
        onClick={() => login()}
        type="button"
        loading={isLoading}
        variant="secondary"
        icon={<img src={iconSvg} width={24} />}
      >
        {isSignupPage ? 'Sign up' : 'Sign in'} with Google
      </Button>
    </div>
  );
}
